import type {
  VerdaHistoryCounts,
  VerdaHistoryInstanceType,
  VerdaHistoryInstanceTypeSpecs,
  VerdaHistoryWindow,
} from '../../../types/models/Verda';

// Pure derivations behind Admin › Verda › Availability history (docs/api-types/admin-verda.md,
// "Availability history"). The backend sends counts and per-slot series strings; every share, ranking,
// heatmap cell and timeline bucket the tab draws is computed here. Every hour and day is UTC.
//
// Reading the figures honestly (service/Verda/CLAUDE.md): a sample says whether Verda could deploy ONE more
// instance of a shape at that moment — the sold-out share is a lower bound on saturation, not a utilisation
// rate; a slot without a sample is no data, never "sold out"; a combination never deployable in the window
// is either not offered there or sold out throughout, and is kept apart from the shares.

export type VerdaMarket = 'on_demand' | 'spot';
export type VerdaShapeScope = 'single-gpu' | 'all';

export const MARKET_LABELS: Record<VerdaMarket, string> = { on_demand: 'On-demand', spot: 'Spot' };

/** The window presets, in days; 31 is the backend's maximum. */
export const WINDOW_PRESETS = [7, 14, 31] as const;
export type VerdaWindowPreset = (typeof WINDOW_PRESETS)[number];

const DAY_MS = 86_400_000;

/** The `days`-day window ending on `anchorTo` (a `YYYY-MM-DD` the backend resolved as "today", UTC). */
export function presetQuery(anchorTo: string, days: number): { from: string; to: string } {
  const to = Date.parse(`${anchorTo}T00:00:00Z`);
  return { from: new Date(to - (days - 1) * DAY_MS).toISOString().slice(0, 10), to: anchorTo };
}

/** A sold-out share over sampling runs, or why there is none. */
export type VerdaShare =
  | { kind: 'share'; value: number; soldOut: number; samples: number }
  /** Never deployable in the window: not offered there, or sold out throughout — the data can't tell. */
  | { kind: 'never'; samples: number }
  | { kind: 'no-data' };

export function soldOutShare(counts: VerdaHistoryCounts, market: VerdaMarket): VerdaShare {
  const available = market === 'on_demand' ? counts.on_demand_available : counts.spot_available;
  if (counts.samples === 0) {
    return { kind: 'no-data' };
  }
  if (available === 0) {
    return { kind: 'never', samples: counts.samples };
  }
  const soldOut = counts.samples - available;
  return { kind: 'share', value: soldOut / counts.samples, soldOut, samples: counts.samples };
}

/** `0.6321` → `"63.2%"`. */
export function formatShare(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/** `H100 ×8`, `CPU node`, or the bare code when the specs are unknown. */
export function shapeLabel(t: VerdaHistoryInstanceTypeSpecs): string {
  if (t.gpu_count === 0) {
    return 'CPU node';
  }
  return t.model !== null && t.gpu_count !== null ? `${t.model} ×${t.gpu_count}` : t.instance_type;
}

/** Single-GPU shapes are the best "pool saturated" signal (multi-GPU shapes fragment first). */
export function inScope(t: VerdaHistoryInstanceTypeSpecs, scope: VerdaShapeScope): boolean {
  return scope === 'all' || t.gpu_count === 1;
}

export interface VerdaRankedShape {
  type: VerdaHistoryInstanceType;
  /** Sold out in EVERY location, over the runs that recorded the type. */
  share: Extract<VerdaShare, { kind: 'share' }>;
}

/**
 * The shapes in scope ranked by how often they were sold out everywhere (highest first; ties by label),
 * and apart, those never deployable anywhere in the window — never drawn as a 100% bar.
 */
export function rankSoldOutEverywhere(
  types: VerdaHistoryInstanceType[],
  market: VerdaMarket,
  scope: VerdaShapeScope,
): { ranked: VerdaRankedShape[]; never: VerdaHistoryInstanceType[] } {
  const ranked: VerdaRankedShape[] = [];
  const never: VerdaHistoryInstanceType[] = [];
  for (const type of types) {
    if (!inScope(type, scope)) {
      continue;
    }
    const share = soldOutShare(type.anywhere, market);
    if (share.kind === 'share') {
      ranked.push({ type, share });
    } else if (share.kind === 'never') {
      never.push(type);
    }
  }
  ranked.sort((a, b) => b.share.value - a.share.value || shapeLabel(a.type).localeCompare(shapeLabel(b.type), 'en'));
  return { ranked, never };
}

// ---- Slot series ------------------------------------------------------------------------------

/** Slots that carry a sample, and how many of them were sold out. */
export interface VerdaSlotTally {
  sampled: number;
  soldOut: number;
}

const tallySlot = (tally: VerdaSlotTally, state: string): void => {
  if (state === '0' || state === '1') {
    tally.sampled += 1;
    tally.soldOut += state === '0' ? 1 : 0;
  }
};

/** The sold-out share of a tally, `null` without a sample. */
export function tallyShare(tally: VerdaSlotTally): number | null {
  return tally.sampled === 0 ? null : tally.soldOut / tally.sampled;
}

/** A series with samples but not one deployable slot: not offered there, or sold out throughout. */
export function neverAvailable(series: string): boolean {
  return !series.includes('1') && series.includes('0');
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** `[weekday Mon…Sun][UTC hour 0…23]` tallies — the demand cycle. */
export function hourOfWeek(series: string, window: VerdaHistoryWindow): VerdaSlotTally[][] {
  const grid = WEEKDAYS.map(() => Array.from({ length: 24 }, () => ({ sampled: 0, soldOut: 0 })));
  const start = Date.parse(window.start);
  const slotMs = window.slot_seconds * 1000;
  for (let i = 0; i < series.length; i++) {
    if (series[i] === '-') {
      continue;
    }
    const at = new Date(start + i * slotMs);
    tallySlot(grid[(at.getUTCDay() + 6) % 7][at.getUTCHours()], series[i]);
  }
  return grid;
}

export interface VerdaDayTally extends VerdaSlotTally {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
  /** Slots expected that day (today: so far). */
  expected: number;
}

/** One tally per UTC day of the window, in order. */
export function dailyTallies(series: string, window: VerdaHistoryWindow): VerdaDayTally[] {
  const perDay = Math.round(86_400 / window.slot_seconds);
  const start = Date.parse(window.start);
  return Array.from({ length: window.days }, (_, day) => {
    const tally: VerdaDayTally = {
      date: new Date(start + day * DAY_MS).toISOString().slice(0, 10),
      expected: Math.max(0, Math.min(perDay, window.slots - day * perDay)),
      sampled: 0,
      soldOut: 0,
    };
    const end = Math.min(series.length, (day + 1) * perDay);
    for (let i = day * perDay; i < end; i++) {
      tallySlot(tally, series[i]);
    }
    return tally;
  });
}

export interface VerdaBucket extends VerdaSlotTally {
  /** Slot range `[fromSlot, toSlot)`. */
  fromSlot: number;
  toSlot: number;
}

/** The series folded into at most `maxBuckets` equal runs of slots — a timeline strip's cells. */
export function timelineBuckets(series: string, maxBuckets: number = 288): VerdaBucket[] {
  const size = Math.max(1, Math.ceil(series.length / maxBuckets));
  const buckets: VerdaBucket[] = [];
  for (let from = 0; from < series.length; from += size) {
    const bucket: VerdaBucket = {
      fromSlot: from,
      toSlot: Math.min(series.length, from + size),
      sampled: 0,
      soldOut: 0,
    };
    for (let i = from; i < bucket.toSlot; i++) {
      tallySlot(bucket, series[i]);
    }
    buckets.push(bucket);
  }
  return buckets;
}

/** The ISO instant (UTC) a slot starts at. */
export function slotStart(window: VerdaHistoryWindow, slot: number): string {
  return new Date(Date.parse(window.start) + slot * window.slot_seconds * 1000).toISOString();
}

/** `"2026-10-31T14:05:00.000Z"` → `"Sat 31 Oct, 14:05 UTC"` (the tab's one time format: always UTC). */
export function formatUtc(iso: string): string {
  const at = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  const month = at.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
  return `${WEEKDAYS[(at.getUTCDay() + 6) % 7]} ${at.getUTCDate()} ${month}, ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())} UTC`;
}

/**
 * The fill of a "how sold out" cell on the tab's single sequential hue (PatternFly chart blue, light → dark;
 * the anchor flips in the dark theme — see `.verda-history` in app.css). `null` share = no sample.
 */
export function heatFill(share: number | null): string {
  if (share === null) {
    return 'var(--verda-history-no-data)';
  }
  const pct = Math.round(Math.min(1, Math.max(0, share)) * 100);
  return `color-mix(in oklab, var(--verda-history-seq-high) ${pct}%, var(--verda-history-seq-low))`;
}
